/**
 * useGTM - Google Tag Manager client-side tracking hook
 * 
 * This hook fetches the GTM configuration from the server and, once the
 * visitor consents, injects the GTM script into public pages. It provides
 * methods for pushing custom events to the dataLayer for tracking in GTM.
 * 
 * GTM allows more flexible tag management, including GA4, Facebook Pixel,
 * LinkedIn Insight, and other marketing tags from a single integration.
 */

import { useEffect, useState, useCallback } from 'react';
import { applyConsentDefault, getConsent, onConsentChange } from '@/lib/consent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function injectContainer(containerId) {
  // Once per page, however many components use the hook.
  if (window.dataLayer?.find((item) => item['gtm.start'])) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

/**
 * The container is CONSENT-GATED (POPIA): it loads only once the visitor
 * accepts the cookie banner (TrackingScripts, which the page renders with
 * `gtmEnabled` from here), or on a later visit after accepting. Declined or
 * not yet asked: nothing from googletagmanager.com loads. Events pushed
 * before that wait in dataLayer, and GTM replays them if it loads.
 *
 * Never loads in the editor preview.
 */
export function useGTM({ preview = false } = {}) {
  const [config, setConfig] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (preview) return undefined;
    let cancelled = false;
    let unsubscribe = () => {};

    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/public/gtm-config`);
        const data = await response.json();
        if (cancelled || !(data.success && data.data.enabled && data.data.container_id)) return;

        setConfig(data.data);
        applyConsentDefault();
        const load = () => {
          injectContainer(data.data.container_id);
          setIsReady(true);
        };
        if (getConsent() === 'granted') {
          load();
        } else {
          unsubscribe = onConsentChange((value) => { if (value === 'granted') load(); });
        }
      } catch (error) {
        console.debug('GTM config fetch failed (non-critical):', error);
      }
    };

    fetchConfig();
    return () => { cancelled = true; unsubscribe(); };
  }, [preview]);

  /**
   * Push a custom event to the dataLayer
   * GTM triggers can be configured to fire tags based on these events
   */
  const pushEvent = useCallback((eventName, eventData = {}) => {
    if (!isReady || !window.dataLayer) return;
    
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }, [isReady]);

  /**
   * The standard LinkHub lead analytics contract.
   *
   *   { event: 'generate_lead', lead_type, lead_identifier }
   *
   * Every recognised lead-generating interaction (successful form submission,
   * tel: click, mailto: click) emits exactly this, and LinkHub's responsibility
   * ends at the push — the globally configured GTM container owns the
   * downstream GA4 routing. There is deliberately NO per-page GA4 measurement
   * ID and no per-lead-source GA4 configuration.
   *
   * Unlike pushEvent this is NOT gated on `isReady`: dataLayer is a plain queue
   * that GTM replays once it loads, so pushing early is correct and means a
   * lead is never dropped because the container hadn't finished loading. Wrapped
   * so an analytics failure can never interrupt the visitor's lead action.
   */
  const trackLead = useCallback((leadType, leadIdentifier) => {
    if (!leadType || !leadIdentifier) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        lead_type: leadType,
        lead_identifier: leadIdentifier,
      });
    } catch {
      /* analytics is secondary to the lead interaction */
    }
  }, []);

  /**
   * Track page view
   * Pushes a virtual pageview event to dataLayer. Returns true once handled
   * (pushed, or page views switched off) and false while GTM isn't ready,
   * so a caller firing once per load knows whether to try again.
   */
  const trackPageView = useCallback((pageTitle, pagePath) => {
    if (!isReady) return false;
    if (!config?.track_page_views) return true;
    
    pushEvent('creativehub_page_view', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: pagePath || window.location.pathname,
    });
    return true;
  }, [isReady, config, pushEvent]);

  /**
   * Track form submission
   * Pushes form submit event to dataLayer
   */
  const trackFormSubmit = useCallback((formId, formName, pageTitle) => {
    if (!isReady || !config?.track_form_submissions) return;
    
    pushEvent('creativehub_form_submit', {
      form_id: formId,
      form_name: formName || formId,
      page_title: pageTitle,
    });
  }, [isReady, config, pushEvent]);

  /**
   * Track bio page view
   * Pushes bio page view event to dataLayer
   */
  const trackBioPageView = useCallback((bioId, displayName, slug) => {
    if (!isReady || !config?.track_bio_events) return;
    
    pushEvent('creativehub_bio_page_view', {
      bio_id: bioId,
      display_name: displayName,
      bio_slug: slug,
      page_location: window.location.href,
    });
  }, [isReady, config, pushEvent]);

  /**
   * Track bio link click
   * Pushes bio link click event to dataLayer
   */
  const trackBioLinkClick = useCallback((bioId, linkTitle, linkUrl) => {
    if (!isReady || !config?.track_bio_events) return;
    
    pushEvent('creativehub_bio_link_click', {
      bio_id: bioId,
      link_title: linkTitle,
      link_url: linkUrl,
    });
  }, [isReady, config, pushEvent]);

  /**
   * Track QR code scan (for client-side confirmation)
   */
  const trackQRScan = useCallback((qrId, qrName, destinationUrl) => {
    if (!isReady || !config?.track_qr_scans) return;
    
    pushEvent('creativehub_qr_scan', {
      qr_id: qrId,
      qr_name: qrName,
      destination_url: destinationUrl,
    });
  }, [isReady, config, pushEvent]);

  /**
   * Track short URL click (for client-side confirmation)
   */
  const trackURLClick = useCallback((urlId, urlTitle, destinationUrl) => {
    if (!isReady || !config?.track_url_clicks) return;
    
    pushEvent('creativehub_url_click', {
      url_id: urlId,
      url_title: urlTitle,
      destination_url: destinationUrl,
    });
  }, [isReady, config, pushEvent]);

  /**
   * Set user properties in dataLayer
   * Useful for segmentation in analytics
   */
  const setUserProperties = useCallback((properties) => {
    if (!window.dataLayer) return;
    
    window.dataLayer.push({
      event: 'creativehub_user_properties',
      user_properties: properties,
    });
  }, []);

  /**
   * Generic event tracking
   * For custom events not covered by specific methods
   */
  const trackEvent = useCallback((eventName, params = {}) => {
    if (!isReady) return;
    pushEvent(eventName, params);
  }, [isReady, pushEvent]);

  return {
    isReady,
    config,
    gtmEnabled: !!config,
    pushEvent,
    trackLead,
    trackPageView,
    trackFormSubmit,
    trackBioPageView,
    trackBioLinkClick,
    trackQRScan,
    trackURLClick,
    setUserProperties,
    trackEvent,
  };
}

// Keep backward compatibility alias
export const useGA4 = useGTM;

export default useGTM;
